import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
// import logo from "./../../images/metropolitan-hospital-logo.png";
import TenetLogo from "./../../images/Tenet_Health_logo.png";
import LogoutIcon from "./../../images/logoutIcon.png";
import Cookies from "universal-cookie";
import Popover from "@mui/material/Popover";
import { isSuperAdminAccess } from "../../Screens/dataSaver";
import { TenantID, PUT, GET } from "./../../Screens/dataSaver";
import { fetchEntityDetails } from "../../utils/generalConfig";
import { ErrorToaster } from "./../../utils/toaster";
import jwt from "jwt-decode";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import style from "./index.module.scss";
const useStyles = makeStyles((theme) => ({
  popover: {
    pointerEvents: "none",
  },
  popoverContent: {
    pointerEvents: "auto",
  },
}));

const HIDE_TIMESHEETS_ENTITIES = ['SMMC', 'PMC', 'BSMH'];
const HIDE_REVIEWS_APPROVALS_ENTITIES = ['PMC'];

/** Every role granted to this user via accessScope (tenant-wide, region-wide,
 *  or site-level) — the current model for how roles get assigned.
 */
const collectAccessScopeRoles = (user) => {
  const scopeRoles = Array.isArray(user?.accessScope?.roles) ? user.accessScope.roles : [];
  const scopeRegions = Array.isArray(user?.accessScope?.regions) ? user.accessScope.regions : [];
  const scopeRegionRoles = scopeRegions.flatMap((r) => (Array.isArray(r?.roles) ? r.roles : []));
  const scopeSiteRoles = scopeRegions
    .flatMap((r) => (Array.isArray(r?.sites) ? r.sites : []))
    .flatMap((s) => (Array.isArray(s?.roles) ? s.roles : []));
  return [...scopeRoles, ...scopeRegionRoles, ...scopeSiteRoles];
};

/** Flattens every role assigned to this user. accessScope is the current
 *  model, so once it carries any roles it's treated as the complete,
 *  authoritative list — the legacy top-level `roles`/`sites.sites[]` fields
 *  are only a fallback for records that predate accessScope and were never
 *  migrated. A user record can retain a stale entry in those legacy fields
 *  after a role change, so they must not be merged in alongside real
 *  accessScope data or a removed role would never stop showing up.
 */
const collectAllRoles = (user) => {
  if (!user) return [];
  const accessScopeRoles = collectAccessScopeRoles(user);
  if (accessScopeRoles.length > 0) return accessScopeRoles;

  const rolesTop = Array.isArray(user?.roles) ? user.roles : [];
  const siteRoles = (user?.sites?.sites || []).flatMap((s) => (Array.isArray(s?.roles) ? s.roles : []));
  return [...rolesTop, ...siteRoles];
};

/** Does this user hold an "Activity Logger" role with rolePerformerTypes
 *  including CONTRACT_SERVICE_PROVIDER_PROXY, at any accessScope level?
 */
const hasActivityLoggerProxyRole = (user) =>
  collectAllRoles(user).some(
    (r) =>
      String(r?.roleName || '').trim().toLowerCase() === 'activity logger' &&
      Array.isArray(r?.rolePerformerTypes) &&
      r.rolePerformerTypes.includes('CONTRACT_SERVICE_PROVIDER_PROXY')
  );

/** The distinct role names held by this user across all accessScope levels. */
const getAllRoleNames = (user) =>
  Array.from(
    new Set(
      collectAllRoles(user)
        .map((r) => String(r?.roleName || '').trim())
        .filter(Boolean)
    )
  );

const Navbar = ({ hideNotifications = false, isFixed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const entityAbbreviation = sessionStorage.getItem('entityAbbreviation') || '';
  const [isContractManager, setIsContractManager] = useState(false);
  const [isWorkflowManager, setIsWorkflowManager] = useState(false);
  const [isEntityLevelAdmin, setIsEntityLevelAdmin] = useState(false);
  const [currentUserRoles, setCurrentUserRoles] = useState([]);
  // Role names pulled from the full user record (region/site accessScope levels
  // aren't in the JWT roles claim). Used as the authoritative role source once
  // loaded; the JWT roles claim is only a bridge before this resolves, or a
  // fallback if it fails — it can carry a stale role left over from before the
  // user's roles last changed, so it must not out-live a successful fetch.
  const [fullAccessScopeRoles, setFullAccessScopeRoles] = useState([]);
  const [accessScopeStatus, setAccessScopeStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // ECATS state management
  let [displayEcats, setDisplayEcats] = useState(null);
  const [, setEcatsLabel] = useState(null);

  const popoverAnchor = useRef(null);
  const popoverAnchormanger = useRef(null);
  const [, setAnchorElTracker] = useState(null);
  const [, setAnchorElHelp] = useState(null);
  const [anchorElTools, setAnchorElTools] = useState(null);
  const openTools = Boolean(anchorElTools);
  const popoverAnchorTools = useRef(null);
  const [logo, setLogo] = useState(sessionStorage?.getItem("logo"));
  const [, setIsActivityServiceLogAvailable] =
    useState(false);
  const [isTimesheetsAvailable, setIsTimesheetsAvailable] = useState(false);
  const [isReviewsAndApprovalsAvailable, setIsReviewsAndApprovalsAvailable] =
    useState(false);
  const [, setIsTaskManagementAvailable] =
    useState(false);
  const [isPaymentsAvailable, setIsPaymentsAvailable] = useState(false);
  const [isContractManagementAvailable, setIsContractManagementAvailable] =
    useState(false);
  const [, setIsContractComplianceAvailable] =
    useState(false);
  const [, setIsContractPerformanceAvailable] =
    useState(false);
  const [, setIsSystemAdministrationAvailable] =
    useState(false);
  const [, setIsSupportAvailable] = useState(false);
  const [entityData, setEntityData] = useState(null);
  const [, setEntityId] = useState(null);
  const [isPmcServicesActivitiesAvailable, setIsPmcServicesActivitiesAvailable] = useState(false);

  let selectedWorkingMode = sessionStorage.getItem("SelectedWorkingMode");

  useEffect(() => {
    closeAllPopovers();
  }, [location.pathname]);

  // Roles assigned at the region/site accessScope level aren't in the JWT roles
  // claim, so the full user record is fetched and merged in (see effect below).
  // This also covers PMC's "Services / Activities" reports menu, which is visible
  // solely to users holding Activity Logger with rolePerformerType CONTRACT_SERVICE_PROVIDER_PROXY.
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const cookie = new Cookies();
        const accessToken = cookie.get('user');
        const userId = accessToken && jwt(accessToken)?.id;
        if (!userId) return;
        const { data } = await GET(`user-management-service/user/${userId}`);
        if (ignore) return;
        setFullAccessScopeRoles(getAllRoleNames(data));
        setAccessScopeStatus('loaded');
        if (entityAbbreviation === 'PMC') {
          setIsPmcServicesActivitiesAvailable(hasActivityLoggerProxyRole(data));
        }
      } catch (e) {
        if (!ignore) {
          setFullAccessScopeRoles([]);
          setAccessScopeStatus('error');
          if (entityAbbreviation === 'PMC') setIsPmcServicesActivitiesAvailable(false);
        }
      }
    })();
    return () => { ignore = true; };
  }, [entityAbbreviation]);

  useEffect(() => {
    const ENTITY_ONLY_ROLES = ['Entity Sys Admin', 'Entity Sys User', 'Super Sys Admin'];
    const selectedRole = localStorage.getItem('flutter.roleSelected')?.replace(/^"|"$/g, '');
    if (ENTITY_ONLY_ROLES.includes(selectedRole)) return;

    if (currentUserRoles?.includes("Activity Logger")) {
      setIsActivityServiceLogAvailable(true);
      setIsContractComplianceAvailable(true);
      setIsContractPerformanceAvailable(true);
      setIsTimesheetsAvailable(true);
    }
    if (
      currentUserRoles?.includes("Reviewer") ||
      currentUserRoles?.includes("Approver") ||
      currentUserRoles?.some(r => r?.toLowerCase()?.replace(/\s/g, '') === "reviewer/approver")
    ) {
      setIsActivityServiceLogAvailable(true);
      setIsContractComplianceAvailable(true);
      setIsContractPerformanceAvailable(true);
      setIsTimesheetsAvailable(true);
    }
    if (currentUserRoles?.includes("Accounts Payable")) {
      setIsContractComplianceAvailable(true);
      setIsContractPerformanceAvailable(true);
    }
    if (
      currentUserRoles?.includes("Invoice Reviewer / Approver") ||
      currentUserRoles?.includes("Finance Reviewer / Approver") ||
      currentUserRoles?.includes("Finance User")
    ) {
      setIsTimesheetsAvailable(true);
      // Payments report is hidden for these roles for TENET only
      if (TenantID !== "66762ba6e1bf6b6a13908869") {
        setIsPaymentsAvailable(true);
      }
    }
    if (currentUserRoles?.includes("Contract Manager")) {
      setIsContractManagementAvailable(true);
      setIsTimesheetsAvailable(true);
      setIsReviewsAndApprovalsAvailable(true);
    }
    if (
      currentUserRoles?.includes("Super Sys Admin") ||
      currentUserRoles?.includes("Entity Sys Admin") ||
      currentUserRoles?.includes("Entity Sys User")
    ) {
      setIsSystemAdministrationAvailable(true);
      setIsSupportAvailable(true);
    }
    if (currentUserRoles?.includes("Distributor Admin")) {
      setIsActivityServiceLogAvailable(true);
      setIsTimesheetsAvailable(true);
      setIsReviewsAndApprovalsAvailable(true);
      setIsTaskManagementAvailable(true);
      setIsPaymentsAvailable(true);
      setIsContractManagementAvailable(true);
      setIsContractComplianceAvailable(true);
      setIsContractPerformanceAvailable(true);
      setIsSystemAdministrationAvailable(true);
      setIsSupportAvailable(true);
    }
    if (
      currentUserRoles?.includes("Contract Business Entity Manager") ||
      currentUserRoles?.includes("Contract Compliance Manager")
    ) {
      setIsContractManagementAvailable(true);
      setIsContractComplianceAvailable(true);
      setIsContractPerformanceAvailable(true);
    }
  }, [currentUserRoles]);

  useEffect(() => {
    setLogo(sessionStorage?.getItem("logo"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStorage?.getItem("logo")]);

  useEffect(() => {
    getReferenceListEntity();
  }, []);

  // const menuRef = useRef(null);
  // const toolsMenuRef = useRef(null);
  // const reportsMenuRef = useRef(null);

  // useMenuHide(menuRef);
  // useToolsMenuHide(toolsMenuRef);
  // useReportsMenuHide(reportsMenuRef);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickTools = (event) => {
    setAnchorElTools(event.currentTarget);
  };

  const handleCloseTools = () => {
    setAnchorElTools(null);
  };

  const [openManager, setOpenManager] = useState(false);
  const handleClickManager = () => setOpenManager(true);
  const handleCloseManager = () => setOpenManager(false);

  const closeAllPopovers = () => {
    setAnchorEl(null);
    setAnchorElTracker(null);
    setAnchorElHelp(null);
    setAnchorElTools(null);
    setOpenManager(false);
  };

  const cookies = new Cookies();

  const logout = async () => {
    await PUT(`logout`)
      .then((response) => {
        const logouturi = response.headers["location"] || "";
        cookies.remove("user", { path: "/" });
        cookies.remove("entityId", { path: "/" });

        window.location.href = logouturi;
      })
      .catch((error) => {
        console.log("error msg", error);
        ErrorToaster("Unexpected Error");
      });
  };

  useEffect(() => {
    var cookie = new Cookies();
    var accessToken = cookie.get("user");
    let jwtRoles = ((accessToken && jwt(accessToken)?.roles?.split(",")) || [])
      .map((r) => r.trim())
      .filter(Boolean);

    // Every role the user actually holds. The freshly-fetched accessScope data
    // (every level: entity/region/site) is authoritative once it loads — trust
    // it alone rather than unioning it with the JWT roles claim, which can
    // carry a role that's since been removed and would otherwise never get
    // cleared out. Only bridge on the JWT while that fetch is still in flight,
    // or fall back to it if the fetch failed outright.
    const heldRoles =
      accessScopeStatus === 'loaded'
        ? fullAccessScopeRoles
        : Array.from(new Set([...jwtRoles, ...fullAccessScopeRoles]));

    // When launched from the Flutter "Select Your Work Mode" screen, nav
    // visibility is scoped to the selected mode, not every role the user holds —
    // e.g. selecting Entity Sys Admin must not leak Contract Manager tabs in,
    // and selecting Contract Manager must not leak Entity Management in.
    const rawRoleSelected = localStorage.getItem('flutter.roleSelected');
    const roleSelectedList = rawRoleSelected
      ?.replace(/^"|"$/g, '')
      ?.split(',')
      ?.map((r) => r.trim())
      ?.filter(Boolean) || [];

    // The stored selection can go stale (e.g. left over from before the user's
    // roles changed), so only trust entries the user still actually holds.
    const validSelectedRoles = roleSelectedList.filter((r) => heldRoles.includes(r));

    let effectiveRoles;
    if (validSelectedRoles.length > 0) {
      effectiveRoles = [...validSelectedRoles];
      // Workflow Manager mode is the one exception that pulls in a second role:
      // if Workflow Manager is selected and the user separately holds Contract
      // Manager too, surface both.
      if (
        validSelectedRoles.includes("Workflow Manager") &&
        heldRoles.includes("Contract Manager") &&
        !effectiveRoles.includes("Contract Manager")
      ) {
        effectiveRoles.push("Contract Manager");
      }
    } else {
      effectiveRoles = [...heldRoles];
    }

    // Contract Manager always bundles in the Workflow Manager tab — this was a
    // single combined capability before Workflow Manager became its own
    // assignable role, so it still shows for Contract Manager users regardless
    // of whether they separately hold that role.
    if (effectiveRoles.includes("Contract Manager") && !effectiveRoles.includes("Workflow Manager")) {
      effectiveRoles.push("Workflow Manager");
    }

    console.log("roles1233", effectiveRoles)
    setCurrentUserRoles(effectiveRoles);
    setIsContractManager(effectiveRoles.includes("Contract Manager") ? true : false);
    setIsWorkflowManager(effectiveRoles.includes("Workflow Manager") ? true : false);
    setIsEntityLevelAdmin(
      effectiveRoles.includes("Super Sys Admin") ||
        effectiveRoles.includes("Entity Sys Admin") ||
        effectiveRoles.includes("Entity Sys User") ||
        effectiveRoles.includes("Distributor Admin")
        ? true
        : false
    );
  }, [fullAccessScopeRoles, accessScopeStatus]);

  // Load ECATS configuration
  useEffect(() => {
    const loadGeneralConfig = async () => {
      try {
        const { fetchGeneralConfig } = await import("../../utils/generalConfig");
        const data = await fetchGeneralConfig();
        if (data) {
          setEcatsLabel(data?.externalContractIdLabel);
          setDisplayEcats(data?.externalContractIdLabel !== "" && data?.externalContractIdLabel != null);
        }
      } catch (e) {
        console.error("Error loading ECATS config:", e);
      }
    };

    loadGeneralConfig();
  }, []);
  
  const getReferenceListEntity = async () => {
    const entity = await fetchEntityDetails({ tenantId: TenantID });
    console.log("entity", entity);
    setEntityData(entity);
    setEntityId(entity?.id);
  };

  const classes = useStyles();

  // Handler to close popover immediately on click and navigate
  const handleMenuItemClick = (closeHandler, navigateTo = null) => {
    closeHandler();
    closeAllPopovers();
    
    if (navigateTo) {
      // Use setTimeout to ensure popover closes before navigation
      setTimeout(() => {
        navigate(navigateTo);
      }, 10);
    }
  };

  let isFlutterRoles = currentUserRoles
    ?.filter((data) =>
      ["Activity Logger", "Accounts Payable", "Approver", "Reviewer"]?.includes(
        data
      )
    )
    ?.map((data) => data);

    console.log("currentUserRoles",currentUserRoles,isFlutterRoles)
    
  let homeLink = currentUserRoles?.includes("Contract Manager")
    ? "/contracts"
    : isFlutterRoles?.length !== 0
    ? `/home/#/dashboardRoute`
    : currentUserRoles?.includes("Super Sys Admin")
    ? "/partnerPortal"
    : currentUserRoles?.includes("Entity Sys Admin")
    ? "/entitySitePortal"
    : "/entitySitePortal";
    
  const homeRoute = () => {
    closeAllPopovers();
    let homeLink = currentUserRoles?.includes("Contract Manager")
      ? "/contracts"
      : isFlutterRoles?.length !== 0
      ? `/`
      : currentUserRoles?.includes("Super Sys Admin")
      ? "/partnerPortal"
      : currentUserRoles?.includes("Entity Sys Admin")
      ? "/entitySitePortal"
      : "/";
    console.log(homeLink);
    if (homeLink === "/") {
      window.location.href = "/home/#/dashboardRoute";
    } else {
      navigate(homeLink);
    }
  };

  return (
    <div className={`${style.navbarStyle} ${isFixed ? style.fixedNavbar : ''}`}>
      <div className={style.spaceBetween}>
        <div className={style.displayInRow}>
          {
            // <img src={SanmateoLogo} alt="Hospital Logo" className={style.logo} />
          }
          <img
            src={
              window.location.pathname.includes("/complianceManager")
                ? TenetLogo
                : logo
            }
            alt="Hospital Logo"
            className={style.sanmateoLogo}
          />
          {/* <div
            className={`${style.menuStyle} ${window.location.pathname.includes(homeLink) &&
              !window.location.pathname.includes(
                "contractsWithABusinessEntity"
              ) &&
              style.activeMenuColor
              } ${window.location.pathname.includes("/complianceManager")
                ? style.activeMenuColor
                : ""
              }`}
            onClick={
              window.location.pathname.includes("/complianceManager")
                ? () => navigate("/complianceManager")
                : homeRoute
            }
          >
            <p>
              HOME -{" "}
              {currentUserRoles?.some((role) =>
                role.toLowerCase().includes("finance")
              )
                ? "FINANCE"
                : window.location.pathname.includes("/complianceManager")
                  ? "COMPLIANCE & AUDIT"
                  : selectedWorkingMode !== null &&
                    selectedWorkingMode !== "" &&
                    selectedWorkingMode !== undefined
                    ? selectedWorkingMode
                    : currentUserRoles?.[0]?.toUpperCase()}
            </p>
          </div> */}
          {isContractManager ? (
            <div>
              {displayEcats ? (
                // Show dropdown when ECATS is enabled
                <div
                  className={`${style.menuStyle} ${
                    (window.location.pathname.includes("/contract-manager") ||
                      window.location.pathname.includes(
                        "/contracts_records_processing"
                      )) &&
                    style.activeMenuColor
                  }`}
                  ref={popoverAnchormanger}
                  onMouseEnter={handleClickManager}
                  onMouseLeave={handleCloseManager}
                  aria-owns={openManager ? "contract-manager-popover" : undefined}
                  aria-haspopup="true"
                >
                  {window.location.pathname.includes(
                    "contracts_records_processing"
                  ) ? (
                    <p
                      style={{
                        width: "200px",
                        textAlign: "start",
                        marginBottom: "-7px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        // color: "inherit"
                      }}
                    >
                      CONTRACT RECORDS PROCESSING
                      {openManager ? (
                        <KeyboardArrowUpIcon style={{ color: "inherit" }} />
                      ) : (
                        <KeyboardArrowDownIcon style={{ color: "inherit" }} />
                      )}
                    </p>
                  ) : (
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        // color: "inherit"
                      }}
                    >
                      CONTRACT MANAGER
                      {openManager ? (
                        <KeyboardArrowUpIcon style={{ color: "inherit" }} />
                      ) : (
                        <KeyboardArrowDownIcon style={{ color: "inherit" }} />
                      )}
                    </p>
                  )}

                  <Popover
                    id="contract-manager-popover"
                    open={openManager}
                    anchorEl={popoverAnchormanger.current}
                    onClose={handleCloseManager}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    classes={{ paper: classes.popoverContent }}
                    PaperProps={{
                      onMouseEnter: handleClickManager,
                      onMouseLeave: handleCloseManager,
                    }}
                  >
                    <div className={style.optionsCardStyle}>
                      <div 
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleCloseManager, "/contract-manager")}
                      >
                        Contract Management
                      </div>
                      <div 
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleCloseManager, "/contracts_records_processing")}
                      >
                        Contract Records Processing
                      </div>
                    </div>
                  </Popover>
                </div>
              ) : (
                // Show direct link when ECATS is disabled
                <div
                  className={`${style.menuStyle} ${
                    window.location.pathname.includes("/contract-manager") &&
                    style.activeMenuColor
                  }`}
                  onClick={() => {
                    closeAllPopovers();
                    navigate("/contract-manager");
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <p>CONTRACT MANAGER</p>
                </div>
              )}
            </div>
          ) : isWorkflowManager ? (
            <div
              className={`${style.menuStyle} ${
                (window.location.pathname === "/workflowManager" ||
                  window.location.pathname.includes("workflowManager")) &&
                style.activeMenuColor
              }`}
              onClick={() => handleMenuItemClick(closeAllPopovers, "/workflowManager")}
              style={{ cursor: 'pointer' }}
            >
              <p>HOME - WORKFLOW MANAGER</p>
            </div>
          ) : (
            <div
              className={`${style.menuStyle} ${
                window.location.pathname.includes(homeLink) &&
                !window.location.pathname.includes(
                  "contractsWithABusinessEntity"
                ) &&
                style.activeMenuColor
              } ${
                window.location.pathname.includes("/complianceManager")
                  ? style.activeMenuColor
                  : ""
              }`}
              onClick={() => {
                closeAllPopovers();
                if (window.location.pathname.includes("/complianceManager")) {
                  navigate("/complianceManager");
                } else {
                  homeRoute();
                }
              }}
            >
              <p>
                HOME -{" "}
                {currentUserRoles?.some((role) =>
                  role.toLowerCase().includes("finance")
                )
                  ? "FINANCE"
                  : window.location.pathname.includes("/complianceManager")
                  ? "COMPLIANCE & AUDIT"
                  // : currentUserRoles?.[0]?.toUpperCase()}
                   : selectedWorkingMode || currentUserRoles?.[0]?.toUpperCase()}
              </p>
            </div>
          )}

          {
            //   isContractManager && (
            //     <Link to={'/contracts'} className={style.noFontStyle}>
            //         <div className={`${style.menuStyle} ${window.location.pathname === "/contracts" && style.activeMenuColor}`}>
            //             <p>CONTRACT MANAGER</p>
            //         </div>
            //     </Link>
            // )
          }
          {/* {
            isContractManager && (
              <div>
                <div
                  className={`${style.menuStyle} ${window.location.pathname.includes("/trackContracts") ?
                    style.activeMenuColor : ''}`}
                  ref={popoverAnchorTracker}
                  onMouseEnter={(e) => handleClickTracker(e)}
                  onMouseLeave={() => handleCloseTracker()}
                  aria-owns={openTracker ? "mouse-over-popover" : undefined}
                  aria-haspopup="true"
                >
                  <p>TRACK YOUR CONTRACTS</p>
                  <Popover
                    id={"mouse-over-popover"}
                    open={openTracker}
                    anchorEl={popoverAnchorTracker.current}
                    onClose={handleCloseTracker}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    classes={{
                      paper: classes.popoverContent,
                    }}
                    PaperProps={{
                      onMouseEnter: handleClickTracker,
                      onMouseLeave: handleCloseTracker,
                    }}
                  >
                    <div
                      className={style.optionsCardStyle}
                      onClick={() => handleCloseTracker()}
                    >
                      <Link
                        to={"/trackContracts/compensationTracker"}
                        className={style.noFontStyle}
                      >
                        <div className={style.options}>
                          Compensation Tracker
                        </div>
                      </Link>
                      <Link
                        to={"/trackContracts/activityStatusTracker"}
                        className={style.noFontStyle}
                      >
                        <div className={style.options}>Track Services Rendered</div>
                      </Link>
                      <Link
                        to={"/trackContracts/timesheetAndInvoiceApprovalsStatusTracker"}
                        className={style.noFontStyle}
                      >
                        <div className={style.options}>Timesheet Submission & Approval Status</div>
                      </Link>
                      <Link
                        to={"/trackContracts/paymentProcessingStatusTracker"}
                        className={style.noFontStyle}
                      >
                        <div className={style.options}>Payment Tracker</div>
                      </Link>
                    </div>
                  </Popover>
                </div>
              </div>
            )
          } */}
          {/* Contract Manager already occupies the home slot above, so add WORKFLOW MANAGER as its own tab only when the user also holds that role */}
          {isContractManager && isWorkflowManager && (
            <div 
              className={`${style.menuStyle} ${
                (window.location.pathname === "/workflowManager" ||
                  window.location.pathname.includes("workflowManager")) &&
                style.activeMenuColor
              }`}
              onClick={() => handleMenuItemClick(closeAllPopovers, "/workflowManager")}
            >
              <p>WORKFLOW MANAGER</p>
            </div>
          )}
          {(isTimesheetsAvailable ||
            isContractManagementAvailable ||
            isReviewsAndApprovalsAvailable ||
            isPaymentsAvailable ||
            isPmcServicesActivitiesAvailable) && (
            <div>
              <div
                className={`${style.menuStyle} ${
                  (window.location.pathname.includes("/reports") ||
                    window.location.pathname.includes("/reportTypeOverview") ||
                    window.location.pathname.includes("/myReport")) &&
                  style.activeMenuColor
                }`}
                ref={popoverAnchor}
                onMouseEnter={(e) => handleClick(e)}
                onMouseLeave={() => handleClose()}
                aria-owns={open ? "mouse-over-popover" : undefined}
                aria-haspopup="true"
              >
                <p>REPORTS</p>
                <Popover
                  id={"mouse-over-popover"}
                  open={open}
                  anchorEl={popoverAnchor.current}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  classes={{
                    paper: classes.popoverContent,
                  }}
                  PaperProps={{
                    onMouseEnter: handleClick,
                    onMouseLeave: handleClose,
                  }}
                >
                  <div className={style.optionsCardStyle}>
                    {isContractManagementAvailable && (
                      <div
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleClose, "/reports/contractManagement")}
                      >
                        Contract Management
                      </div>
                    )}
                    {isTimesheetsAvailable && !HIDE_TIMESHEETS_ENTITIES.includes(entityAbbreviation) && (
                      <div
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleClose, "/reports/timesheets")}
                      >
                        Timesheets
                      </div>
                    )}
                    {isReviewsAndApprovalsAvailable && !HIDE_REVIEWS_APPROVALS_ENTITIES.includes(entityAbbreviation) && (
                      <div
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleClose, "/reports/reviewsApprovals")}
                      >
                        Reviews & Approvals
                      </div>
                    )}
                    {isPaymentsAvailable && (
                      <div
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleClose, "/reports/payments")}
                      >
                        Payments
                      </div>
                    )}
                    {isPmcServicesActivitiesAvailable && (
                      <div
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleClose, "/reports/servicesOrActivities")}
                      >
                        Services / Activities Log Reports
                      </div>
                    )}
                  </div>
                </Popover>
              </div>
            </div>
          )}
          
          {isEntityLevelAdmin && (
            <div>
              <div
                className={`${style.menuStyle} ${
                  (window.location.pathname === "/user" ||
                    window.location.pathname === "/welcome" ||
                    window.location.pathname === "/partnerPortal") &&
                  style.activeMenuColor
                }`}
                ref={popoverAnchorTools}
                onMouseEnter={(e) => handleClickTools(e)}
                onMouseLeave={() => handleCloseTools()}
                aria-owns={openTools ? "mouse-over-popover" : undefined}
                aria-haspopup="true"
              >
                <p>TOOLS</p>
                <Popover
                  id={"mouse-over-popover"}
                  open={openTools}
                  anchorEl={popoverAnchorTools.current}
                  onClose={handleCloseTools}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  classes={{
                    paper: classes.popoverContent,
                  }}
                  PaperProps={{
                    onMouseEnter: handleClickTools,
                    onMouseLeave: handleCloseTools,
                  }}
                >
                  <div className={style.optionsCardStyle}>
                    {currentUserRoles?.includes("Super Sys Admin") && (
                      <div 
                        className={`${style.options} ${style.cursorPointer}`}
                        onClick={() => handleMenuItemClick(handleCloseTools, "/referenceList")}
                      >
                        REFERENCE LIST
                      </div>
                    )}

                    <div 
                      className={`${style.options} ${style.cursorPointer}`}
                      onClick={() => handleMenuItemClick(handleCloseTools, 
                        isSuperAdminAccess
                          ? "/partnerPortal"
                          : `/entitySetup/${TenantID}/appSubscription`
                      )}
                    >
                      ENTITY MANAGEMENT
                    </div>
                    <div 
                      className={`${style.options} ${style.cursorPointer}`}
                      onClick={() => handleMenuItemClick(handleCloseTools, 
                        entityData?.multiSiteEntity
                          ? "/alerts-notifications"
                          : "/alerts-notifications"
                      )}
                    >
                      ALERTS & NOTIFICATIONS SETUP
                    </div>
                  </div>
                </Popover>
              </div>
            </div>
          )}
          
          {/* <div>
            <div
              className={`${style.menuStyle} ${
                window.location.pathname === "/help" && style.activeMenuColor
              }`}
              ref={popoverAnchorHelp}
              onMouseEnter={(e) => handleClickHelp(e)}
              onMouseLeave={() => handleCloseHelp()}
              aria-owns={"mouse-over-popover"}
              aria-haspopup="true"
            >
              <p>HELP</p>
              <Popover
                id={"mouse-over-popover"}
                open={openHelp}
                anchorEl={popoverAnchorHelp.current}
                onClose={handleCloseHelp}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                classes={{
                  paper: classes.popoverContent,
                }}
                PaperProps={{
                  onMouseEnter: handleClickHelp,
                  onMouseLeave: handleCloseHelp,
                }}
              >
                <div className={style.optionsCardStyle}>
                  <div
                    className={`${style.options} ${style.cursorPointer}`}
                    onClick={() => {
                      handleMenuItemClick(handleCloseHelp);
                      handleScreenshot();
                    }}
                  >
                    OPEN FEEDBACK TICKET
                  </div>
                  <div
                    className={`${style.options} ${style.cursorPointer}`}
                    onClick={() => handleMenuItemClick(handleCloseHelp, "/help")}
                  >
                    SUPPORT PORTAL
                  </div>
                </div>
              </Popover>
            </div>
          </div> */}
        </div>
        <div className={style.displayInRow}>
          {/* Notifications Icon and Count */}
          {!hideNotifications ? (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                marginRight: "16px",
              }}
            >
              {/* <img
                src={NotificationsIcon}
                alt="notifications"
                className={style.icons}
                style={{ width: 40, height: 40 }}
              /> */}
              {/* <img
                src={RedBackground}
                alt="notification background"
                style={{
                  position: "absolute",
                  right: 0,
                  top: 8,
                  width: 28,
                  height: 28,
                  zIndex: 1,
                }}
              /> */}
              {/* <span
                style={{
                  position: "absolute",
                  right: 0,
                  top: 8,
                  width: 28,
                  height: 28,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                13
              </span> */}
            </div>
          ) : (
            // Placeholder to preserve layout/spacing exactly
            <div
              style={{
                width: 56,         // 40 icon + 16 right margin visual space
                height: 40,
                marginRight: "16px",
              }}
            />
          )}
          <div
            className={`${style.logoutStyle} ${style.cursorPointer}`}
            onClick={logout}
          >
            <p>Logout</p>
          </div>
          <img
            src={LogoutIcon}
            alt="print"
            className={style.logoutIcons}
            onClick={logout}
          />
        </div>
      </div>
    </div>
  );
};

export default Navbar;